/**
 * i18next custom formatters — locale-aware date/time/number formatting.
 *
 * Slack's hard-won lesson: never embed ICU date format spec inside a
 * sentence — translators have to learn a parallel mini-language and
 * mistakes blow up downstream. Instead: define named formats in
 * `formats.json` per-locale, pass dates/numbers as `formatParams`, and
 * let the FE do the locale-correct formatting via `Intl.*`.
 *
 * Usage in catalogs:
 *
 *   "card.created": "Created {createdAt, datetime, ::yMMMd}"
 *   "card.duration": "{minutes, number, ::compact-short} min"
 *
 * Or via `formatParams` with named formats from `formats.json`:
 *
 *   t('card.created', { createdAt, formatParams: { createdAt: 'short-date' } })
 */

import type { i18n } from 'i18next'

// Module-scope cache keyed on a string. The prior WeakMap was keyed on
// the `options` parameter reference — but every call creates a fresh
// object literal, so the WeakMap never hit. Bounded growth comes from
// the small surface area of (locale, options-spec) pairs (~50 across
// the supported locales × format-spec variants used in catalogs).
const dateFormatterCache = new Map<string, Intl.DateTimeFormat>()

/** Memoize Intl.DateTimeFormat instances by (locale, options) — they're surprisingly expensive to construct. */
const dateFormatter = (
  locale: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat => {
  const key = `${locale}|${JSON.stringify(options)}`
  let cached = dateFormatterCache.get(key)
  if (!cached) {
    cached = new Intl.DateTimeFormat(locale, options)
    dateFormatterCache.set(key, cached)
  }
  return cached
}

const numberFormatter = (locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat =>
  new Intl.NumberFormat(locale, options)

const relativeTimeFormatter = (locale: string): Intl.RelativeTimeFormat =>
  new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })

/** Convert a value to Date if it isn't one already (catalogs often pass ISO strings). */
const toDate = (v: unknown): Date | null => {
  if (v instanceof Date) return v
  if (typeof v === 'string' || typeof v === 'number') {
    const d = new Date(v)
    return Number.isNaN(d.getTime()) ? null : d
  }
  return null
}

/**
 * Compute "2h ago" / "in 3 days" relative-time string. Direct call,
 * bypasses the `t()` / ICU layer — `i18next-icu` v2 only handles its
 * own built-in ICU types (`date`, `time`, `number`, `plural`, `select`)
 * and does NOT delegate custom names like `relativeTime` to
 * `i18n.services.formatter`, so the catalog template falls back to
 * the literal `{x, relativeTime}` on render. Components needing this
 * call here directly.
 */
export const formatRelativeTime = (value: unknown, locale: string): string => {
  const d = toDate(value)
  if (!d || !locale) return ''
  const diffMs = d.getTime() - Date.now()
  const absMs = Math.abs(diffMs)
  const formatter = relativeTimeFormatter(locale)
  if (absMs < 60_000) return formatter.format(Math.round(diffMs / 1000), 'second')
  if (absMs < 3_600_000) return formatter.format(Math.round(diffMs / 60_000), 'minute')
  if (absMs < 86_400_000) return formatter.format(Math.round(diffMs / 3_600_000), 'hour')
  if (absMs < 30 * 86_400_000) return formatter.format(Math.round(diffMs / 86_400_000), 'day')
  if (absMs < 365 * 86_400_000)
    return formatter.format(Math.round(diffMs / (30 * 86_400_000)), 'month')
  return formatter.format(Math.round(diffMs / (365 * 86_400_000)), 'year')
}

/**
 * Locale-correct percentage from a 0..1 fraction — "60%", "60 %" in
 * Spanish, and the right digits everywhere else.
 *
 * A direct call for the same reason `formatRelativeTime` is one: the
 * registered `percent` formatter is reachable only through i18next's own
 * `{{value, percent}}` interpolation, and these catalogs are ICU, where
 * double braces are an escaped literal. Upload progress also has no
 * sentence to sit in — it is a bare number beside a bar — so routing it
 * through a catalog key would buy a translator nothing and cost every
 * namespace a duplicate entry.
 */
export const formatPercent = (fraction: number, locale: string): string => {
  if (!Number.isFinite(fraction) || !locale) return ''
  return numberFormatter(locale, { style: 'percent', maximumFractionDigits: 0 }).format(fraction)
}

/**
 * Locale-correct compact count — "1.2K", "3.4M" ("1,2 mil" in Spanish). A
 * direct call for the same reason as `formatPercent`: token counts sit in a
 * tight table cell with no sentence to embed a `{n, compactNumber}` token in.
 */
export const formatCompactNumber = (value: number, locale: string): string => {
  if (!Number.isFinite(value) || !locale) return ''
  return numberFormatter(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(value)
}

/**
 * Locale-correct time-of-day ("4:15 PM", "16:15" in most other locales) —
 * a direct call, same reasoning as `formatRelativeTime` / `formatPercent`:
 * the registered `shortTime` formatter is reachable only through an ICU
 * catalog template, and a compact card's event-start time sits beside a
 * title with no sentence to embed a `{startAt, shortTime}` token into.
 * Mirrors the registered `shortTime` formatter's options exactly so the
 * two never drift.
 */
export const formatShortTime = (value: unknown, locale: string): string => {
  const d = toDate(value)
  if (!d || !locale) return ''
  return dateFormatter(locale, { timeStyle: 'short' }).format(d)
}

/**
 * Locale-correct time-of-day in a named IANA zone — "7:00 PM" for a UTC
 * midnight viewed from New York. A direct call, same reasoning as
 * `formatShortTime`; an unknown zone falls back to the browser's.
 */
export const formatShortTimeInZone = (value: unknown, locale: string, timeZone: string): string => {
  const d = toDate(value)
  if (!d || !locale) return ''
  try {
    return dateFormatter(locale, { timeStyle: 'short', timeZone }).format(d)
  } catch {
    return dateFormatter(locale, { timeStyle: 'short' }).format(d)
  }
}

/** Short weekday name ("Mon", "lun") of an instant in a named IANA zone. */
export const formatShortWeekdayInZone = (
  value: unknown,
  locale: string,
  timeZone: string,
): string => {
  const d = toDate(value)
  if (!d || !locale) return ''
  try {
    return dateFormatter(locale, { weekday: 'short', timeZone }).format(d)
  } catch {
    return dateFormatter(locale, { weekday: 'short' }).format(d)
  }
}

/**
 * Locale-correct medium date ("Nov 1, 2026"). A direct call: the registered
 * `mediumDate` formatter is not reachable from the ICU catalogs, and a
 * "since {date}" fragment has no other sentence to embed it in.
 */
export const formatMediumDate = (value: unknown, locale: string): string => {
  const d = toDate(value)
  if (!d || !locale) return ''
  return dateFormatter(locale, { dateStyle: 'medium' }).format(d)
}

const listFormatterCache = new Map<string, Intl.ListFormat>()

/**
 * Locale-correct conjunction list — "Wednesday and Friday",
 * "Monday, Wednesday, and Friday", "miércoles y viernes". A direct call
 * for the same reason as `formatRelativeTime`: the joiner ("and" / "y",
 * Oxford comma or not) is locale grammar, not a catalog string, and the
 * items are already-translated values with no sentence to embed in.
 */
export const formatList = (items: readonly string[], locale: string): string => {
  if (items.length === 0 || !locale) return ''
  let formatter = listFormatterCache.get(locale)
  if (!formatter) {
    formatter = new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' })
    listFormatterCache.set(locale, formatter)
  }
  return formatter.format(items)
}

/** Wire all named formatters onto an existing i18next instance. */
export const registerFormatters = (i18n: i18n): void => {
  // ---- Date / time named formats. Pull `formats.json` named keys via
  // chained format syntax (i18next's `format` callback receives the value
  // and the format name; we consult `formats.json` per-locale to map
  // names → Intl options).
  i18n.services.formatter?.add('shortDate', (value, lng, _options) => {
    const d = toDate(value)
    if (!d || !lng) return ''
    return dateFormatter(lng, { dateStyle: 'short' }).format(d)
  })

  i18n.services.formatter?.add('mediumDate', (value, lng, _options) => {
    const d = toDate(value)
    if (!d || !lng) return ''
    return dateFormatter(lng, { dateStyle: 'medium' }).format(d)
  })

  i18n.services.formatter?.add('longDate', (value, lng, _options) => {
    const d = toDate(value)
    if (!d || !lng) return ''
    // Bare `en` is locale-agnostic and Intl falls back differently on
    // different platforms (en-GB on some Linux Node builds, en-US on
    // most browsers). Pin it to en-US so "Month DD, YYYY" is the
    // canonical English ordering — explicit en-GB / en-AU users keep
    // their regional preference.
    const locale = lng === 'en' ? 'en-US' : lng
    // `dateStyle: 'long'` keeps Spanish + future locales locale-correct;
    // pinning explicit month/day/year components would freeze ordering
    // across every language. The en→en-US bridge above is the only
    // English-specific concession.
    return dateFormatter(locale, { dateStyle: 'long' }).format(d)
  })

  i18n.services.formatter?.add('shortTime', (value, lng, _options) => {
    const d = toDate(value)
    if (!d || !lng) return ''
    return dateFormatter(lng, { timeStyle: 'short' }).format(d)
  })

  i18n.services.formatter?.add('dateTime', (value, lng, _options) => {
    const d = toDate(value)
    if (!d || !lng) return ''
    return dateFormatter(lng, { dateStyle: 'medium', timeStyle: 'short' }).format(d)
  })

  i18n.services.formatter?.add('longDateTime', (value, lng, _options) => {
    const d = toDate(value)
    if (!d || !lng) return ''
    return dateFormatter(lng, { dateStyle: 'long', timeStyle: 'short' }).format(d)
  })

  i18n.services.formatter?.add('weekday', (value, lng, _options) => {
    const d = toDate(value)
    if (!d || !lng) return ''
    return dateFormatter(lng, { weekday: 'long' }).format(d)
  })

  // ---- Relative time. Catalogs like:
  //   "comment.posted": "Posted {createdAt, relativeTime}"
  i18n.services.formatter?.add('relativeTime', (value, lng, _options) => {
    const d = toDate(value)
    if (!d || !lng) return ''
    const diffMs = d.getTime() - Date.now()
    const absMs = Math.abs(diffMs)
    const formatter = relativeTimeFormatter(lng)
    if (absMs < 60_000) return formatter.format(Math.round(diffMs / 1000), 'second')
    if (absMs < 3_600_000) return formatter.format(Math.round(diffMs / 60_000), 'minute')
    if (absMs < 86_400_000) return formatter.format(Math.round(diffMs / 3_600_000), 'hour')
    if (absMs < 30 * 86_400_000) return formatter.format(Math.round(diffMs / 86_400_000), 'day')
    if (absMs < 365 * 86_400_000)
      return formatter.format(Math.round(diffMs / (30 * 86_400_000)), 'month')
    return formatter.format(Math.round(diffMs / (365 * 86_400_000)), 'year')
  })

  // ---- Numbers. The ICU built-in `number` format goes through
  // intl-messageformat already; these named extensions cover common
  // SaaS contexts (compact for big counts, percent for ratios).
  i18n.services.formatter?.add('compactNumber', (value, lng, _options) => {
    if (typeof value !== 'number' || !lng) return String(value ?? '')
    return numberFormatter(lng, { notation: 'compact', compactDisplay: 'short' }).format(value)
  })

  i18n.services.formatter?.add('percent', (value, lng, _options) => {
    if (typeof value !== 'number' || !lng) return String(value ?? '')
    return numberFormatter(lng, { style: 'percent', maximumFractionDigits: 1 }).format(value)
  })
}
